import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Button, cn } from "@patient-watch/ui";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNextButton,
    CarouselPreviousButton,
} from "@patient-watch/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { AlertCircle, Loader2, Palmtree, Ship } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import { z } from "zod";
import { PageLayout } from "../components/PageLayout";
import { useProfileEntries } from "../hooks/useProfileEntries";
import { useCreateEntry } from "../hooks/useCreateEntry";
import { CalendarMonth } from "../components/CalendarMonth";
import { useState } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { useCopy } from "../i18n/locale";

const searchSchema = z.object({
    profile_id: z.string().uuid(),
});

export const Route = createFileRoute("/entries")({
    component: EntriesComponent,
    validateSearch: (search) => searchSchema.parse(search),
});

interface SelectedEntry {
    date: Dayjs;
    entryId?: string;
}

function EntriesComponent() {
    const { profile_id } = useSearch({ from: "/entries" });
    const navigate = useNavigate();
    const [selectedEntries, setSelectedEntries] = useState<SelectedEntry[]>([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showFutureWarning, setShowFutureWarning] = useState(false);
    const createEntry = useCreateEntry();
    const copy = useCopy();

    const { data: entries = [], isLoading, isError, refetch } = useProfileEntries(profile_id);

    const handleSelectDate = (date: Dayjs, entryId?: string) => {
        // Check if date is in the future
        if (date.isAfter(dayjs(), "day")) {
            setShowFutureWarning(true);
            return;
        }

        setSelectedEntries((prev) => {
            const existingIndex = prev.findIndex((e) => e.date.isSame(date, "day"));
            if (existingIndex >= 0) {
                // Deselect if already selected
                return prev.filter((_, i) => i !== existingIndex);
            }
            // Add to selection
            return [...prev, { date, entryId }];
        });
    };

    const handleMarkAsNotAtSea = async () => {
        if (selectedEntries.length === 0) {
            toast.error(copy.entries.noneSelected);
            return;
        }

        setIsUpdating(true);
        try {
            // First, create entries for dates that don't have them
            const entryIds: string[] = [];
            for (const entry of selectedEntries) {
                if (entry.entryId) {
                    entryIds.push(entry.entryId);
                } else {
                    // Create entry for this date
                    const result = await createEntry.mutateAsync({
                        profileId: profile_id,
                        date: entry.date,
                    });
                    entryIds.push(result.entryId);
                }
            }

            // Update all entries
            const { supabaseClient } = await import("../utils/supabase");
            await Promise.all(
                entryIds.map(async (entryId) => {
                    const { error } = await supabaseClient
                        .from("entries")
                        .update({
                            at_sea: false,
                            glucose_issue: "none",
                            submitted_at: new Date().toISOString(),
                        })
                        .eq("entry_id", entryId);
                    if (error) throw error;
                })
            );

            toast.success(copy.entries.markedNotAtSea(entryIds.length));
            refetch();
            setSelectedEntries([]);
        } catch (error) {
            console.error("Bulk update error:", error);
            toast.error(copy.entries.updateFailed);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleMarkAsAtSea = async () => {
        if (selectedEntries.length === 0) {
            toast.error(copy.entries.noneSelected);
            return;
        }

        setIsUpdating(true);
        try {
            // First, create entries for dates that don't have them
            const entryIds: string[] = [];
            for (const entry of selectedEntries) {
                if (entry.entryId) {
                    entryIds.push(entry.entryId);
                } else {
                    // Create entry for this date
                    const result = await createEntry.mutateAsync({
                        profileId: profile_id,
                        date: entry.date,
                    });
                    entryIds.push(result.entryId);
                }
            }

            // Update all entries
            const { supabaseClient } = await import("../utils/supabase");
            await Promise.all(
                entryIds.map(async (entryId) => {
                    const { error } = await supabaseClient
                        .from("entries")
                        .update({
                            at_sea: true,
                            at_sea_all_day: null,
                            at_sea_intervals: null,
                            on_duty_intervals: null,
                            glucose_issue: null,
                            submitted_at: null,
                        })
                        .eq("entry_id", entryId);
                    if (error) throw error;
                })
            );

            toast.success(copy.entries.markedAtSea(entryIds.length));
            refetch();
            setSelectedEntries([]);
        } catch (error) {
            console.error("Bulk update error:", error);
            toast.error(copy.entries.updateFailed);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleOpenSingleEntry = async () => {
        if (selectedEntries.length !== 1) {
            toast.error(copy.entries.openOne);
            return;
        }

        const entry = selectedEntries[0];
        let entryId = entry.entryId;

        // If no entry exists for this date, create one
        if (!entryId) {
            try {
                setIsUpdating(true);
                const result = await createEntry.mutateAsync({
                    profileId: profile_id,
                    date: entry.date,
                });
                entryId = result.entryId;
            } catch (error) {
                console.error("Failed to create entry:", error);
                toast.error(copy.entries.createFailed);
                return;
            } finally {
                setIsUpdating(false);
            }
        }

        navigate({
            to: "/form",
            search: { entry_id: entryId, profile_id } as any,
        });
    };

    if (isLoading) {
        return (
            <PageLayout
                title={copy.entries.loadingTitle}
                description={copy.entries.loadingDescription}
                icon={Loader2}
                theme="blue"
                isLoading
            >
                <p className="text-slate-600 text-sm text-center">{copy.entries.pleaseWait}</p>
            </PageLayout>
        );
    }

    if (isError) {
        return (
            <PageLayout
                title={copy.entries.errorTitle}
                description={copy.entries.errorDescription}
                icon={AlertCircle}
                theme="pink"
            >
                <Button onClick={() => refetch()} variant="outline">
                    {copy.common.tryAgain}
                </Button>
            </PageLayout>
        );
    }

    // Generate array of months to display (last 6 months + current + next 6 months)
    const months = Array.from({ length: 13 }, (_, i) => {
        return dayjs()
            .subtract(6 - i, "month")
            .startOf("month");
    });

    // Find the index of the current month
    const currentMonth = dayjs().startOf("month");
    const currentMonthIndex = months.findIndex((month) => month.isSame(currentMonth, "month"));

    const selectedDates = selectedEntries.map((e) => e.date);

    // Determine if selected entries are "not at sea/harbour" (at_sea: false, submitted)
    const selectedEntriesData = selectedEntries
        .map((se) => entries.find((e) => e.entry_id === se.entryId))
        .filter(Boolean);

    const allSelectedAreNotAtSea =
        selectedEntriesData.length > 0 && selectedEntriesData.every((e) => e?.at_sea === false && e?.submitted_at);

    return (
        <PageLayout title={copy.entries.title} description={copy.entries.description} theme="blue">
            <Carousel opts={{ startIndex: currentMonthIndex }}>
                <div className="space-y-6 pb-6">
                    {/* Calendar Carousel */}
                    <div className="bg-white shadow-sm p-4 border border-slate-200 rounded-lg">
                        <CarouselContent>
                            {months.map((month) => (
                                <CarouselItem key={month.format("YYYY-MM")}>
                                    <CalendarMonth
                                        month={month}
                                        entries={entries}
                                        selectedDates={selectedDates}
                                        onSelectDate={handleSelectDate}
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        {/* Legend */}
                        <div className="mt-6 pt-4 border-slate-200 border-t">
                            <p className="mb-3 font-semibold text-slate-600 text-sm">{copy.entries.legend}</p>
                            <div className="flex flex-wrap gap-4 text-slate-700 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="bg-green-600 rounded-full w-2 h-2"></div>
                                    <span>{copy.entries.complete}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="bg-amber-600 rounded-full w-2 h-2"></div>
                                    <span>{copy.entries.incomplete}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-base decoration-2 underline underline-offset-2">
                                        1
                                    </span>
                                    <span>{copy.entries.today}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {entries.length === 0 && (
                        <div className="bg-white p-6 border border-slate-300 border-dashed rounded-lg text-slate-600 text-center">
                            <p className="font-medium">{copy.entries.emptyTitle}</p>
                            <p className="mt-2 text-sm">{copy.entries.emptyBody}</p>
                        </div>
                    )}

                    {/* Navigation buttons at bottom */}
                    <div className="flex justify-center gap-3">
                        <CarouselPreviousButton variant="outline" />
                        <CarouselNextButton variant="outline" />
                    </div>
                </div>
            </Carousel>

            {/* Sticky Action Bar */}
            <div className={cn("md:hidden transition-all duration-300", selectedEntries.length > 0 ? "h-24" : "h-0")} />

            {selectedEntries.length > 0 && (
                <div className="right-0 bottom-0 md:bottom-6 left-0 md:left-1/2 z-50 fixed md:max-w-2xl md:-translate-x-1/2">
                    <div className="bg-white/50 backdrop-blur-md p-4 md:border border-slate-200 border-t md:rounded-lg">
                        <div className="space-y-3">
                            {/* Selection info */}
                            <div className="flex justify-between items-center">
                                <p className="font-medium text-slate-700 text-sm">
                                    {copy.entries.daysSelected(selectedEntries.length)}
                                </p>
                                <Button onClick={() => setSelectedEntries([])} variant="ghost" size="sm">
                                    {copy.entries.clear}
                                </Button>
                            </div>

                            {/* Action buttons */}
                            {selectedEntries.length === 1 ? (
                                <Button
                                    onClick={handleOpenSingleEntry}
                                    variant="default"
                                    size="lg"
                                    className="w-full"
                                    disabled={isUpdating || createEntry.isPending}
                                >
                                    {createEntry.isPending
                                        ? copy.entries.creating
                                        : selectedEntries[0]?.entryId
                                          ? copy.entries.open
                                          : copy.entries.createAndOpen}
                                </Button>
                            ) : (
                                <div className="space-y-2">
                                    <p className="font-medium text-md text-slate-700">{copy.entries.markAs}</p>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleMarkAsAtSea}
                                            variant={allSelectedAreNotAtSea ? "default" : "outline"}
                                            size="md"
                                            className="flex-1 px-2"
                                            disabled={isUpdating}
                                        >
                                            <Ship className="w-5 lg:w-6 h-5 lg:h-6" /> {copy.entries.atSea}
                                        </Button>
                                        <Button
                                            onClick={handleMarkAsNotAtSea}
                                            variant={!allSelectedAreNotAtSea ? "default" : "outline"}
                                            size="md"
                                            className="flex-1 px-2"
                                            disabled={isUpdating}
                                        >
                                            <Palmtree className="w-5 lg:w-6 h-5 lg:h-6" /> {copy.entries.notAtSea}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Future Date Warning Dialog */}
            <Dialog open={showFutureWarning} onOpenChange={setShowFutureWarning}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{copy.entries.futureTitle}</DialogTitle>
                        <DialogDescription>{copy.entries.futureBody}</DialogDescription>
                    </DialogHeader>
                    <Button onClick={() => setShowFutureWarning(false)} className="w-full">
                        {copy.entries.ok}
                    </Button>
                </DialogContent>
            </Dialog>
        </PageLayout>
    );
}
